import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Clock, Database, AlertTriangle, GitBranch, RefreshCw } from "lucide-react";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function PipelineDetailsPage() {
  const { pipelineId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/pipelines/${pipelineId}`).then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [pipelineId]);

  if (loading) return (
    <div className="p-6 space-y-4" data-testid="pipeline-details-loading">
      <Skeleton className="h-8 w-64" /><Skeleton className="h-48" /><Skeleton className="h-64" />
    </div>
  );

  if (!data) return <div className="p-6 text-center text-slate-600 font-mono">[ PIPELINE NOT FOUND ]</div>;

  const runs = (data.runs || []).slice().reverse();
  const rowCountData = runs.map((r) => ({ date: r.run_started_at?.slice(5, 10), rows: r.row_count, status: r.status }));
  const freshnessData = runs.map((r) => ({ date: r.run_started_at?.slice(5, 10), lag: r.freshness_lag_minutes, sla: data.sla_minutes }));

  const qualityByMetric = {};
  (data.quality_checks || []).forEach((q) => {
    if (!qualityByMetric[q.metric_name]) qualityByMetric[q.metric_name] = [];
    qualityByMetric[q.metric_name].push(q);
  });

  const recentNullRates = (qualityByMetric["null_rate"] || []).slice(-30).map((q) => ({ date: q.observed_at?.slice(5, 10), value: q.metric_value, threshold: q.threshold_value }));
  const recentDupRates = (qualityByMetric["duplicate_rate"] || []).slice(-30).map((q) => ({ date: q.observed_at?.slice(5, 10), value: q.metric_value, threshold: q.threshold_value }));

  const schemas = data.schema_snapshots || [];
  const incidents = data.incidents || [];

  const impacted = data.lineage?.filter((e) => e.source_asset === data.destination_asset).map((e) => e.target_asset) || [];

  return (
    <div className="p-6 space-y-6" data-testid="pipeline-details-page">
      {/* Header */}
      <div>
        <Link to="/pipelines" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="back-to-pipelines">
          <ArrowLeft className="w-4 h-4" /> Pipelines
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-mono text-[#2D3142]">{data.name}</h1>
              <span className={`status-dot ${data.status}`} />
              <Badge variant={data.status === "critical" ? "destructive" : data.status === "warning" ? "outline" : "secondary"} className="uppercase text-[10px]">{data.status}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Database className="w-3.5 h-3.5" />{data.source_system} &rarr; {data.destination_asset}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />SLA: {data.sla_minutes}m</span>
              <span>Owner: <span className="font-medium text-foreground">{data.owner}</span></span>
              <span className="font-mono text-xs">{data.schedule}</span>
            </div>
          </div>
          <div className="flex gap-2 text-sm">
            <div className="bg-card border border-border px-3 py-2 rounded-sm text-center">
              <div className={`text-lg font-black ${data.freshness_lag_minutes > data.sla_minutes ? "text-red-500" : "text-emerald-500"}`}>{data.freshness_lag_minutes}m</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Lag</div>
            </div>
            <div className="bg-card border border-border px-3 py-2 rounded-sm text-center">
              <div className="text-lg font-black font-mono">{(data.last_row_count || 0).toLocaleString()}</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Rows</div>
            </div>
            <div className="bg-card border border-border px-3 py-2 rounded-sm text-center">
              <div className="text-lg font-black font-mono">{data.schema_version}</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Schema</div>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      <Tabs defaultValue="metrics" className="w-full">
        <TabsList className="bg-muted/50" data-testid="pipeline-tabs">
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="quality">Quality</TabsTrigger>
          <TabsTrigger value="schema">Schema</TabsTrigger>
          <TabsTrigger value="incidents">Incidents ({incidents.length})</TabsTrigger>
          <TabsTrigger value="lineage">Downstream</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glow-card bg-white rounded-lg p-4" data-testid="row-count-chart">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-400/80 mb-3">Row Count (30 Days)</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={rowCountData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#475569" }} stroke="#E8E4DF" />
                    <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#475569" }} stroke="#E8E4DF" />
                    <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E8E4DF", borderRadius: "8px", fontSize: "12px", fontFamily: "JetBrains Mono", color: "#E2E8F0" }} />
                    <Area type="monotone" dataKey="rows" stroke="#29B5E8" fill="rgba(30,58,95,0.08)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="glow-card bg-white rounded-lg p-4" data-testid="freshness-chart">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-400/80 mb-3">Freshness Lag vs SLA</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={freshnessData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "2px", fontSize: "12px", fontFamily: "JetBrains Mono" }} />
                    <Line type="monotone" dataKey="lag" stroke="#D97706" strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="sla" stroke="#DC2626" strokeWidth={1} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          {/* Run History */}
          <div className="bg-card border border-border rounded-sm" data-testid="run-history">
            <div className="px-4 py-3 border-b border-border"><h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Recent Runs</h3></div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-sm data-table">
                <thead className="bg-muted/50 sticky top-0"><tr>
                  <th className="px-4 py-2 text-left">Run ID</th><th className="px-4 py-2 text-left">Started</th><th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Rows</th><th className="px-4 py-2 text-right">Runtime</th><th className="px-4 py-2 text-right">Lag</th>
                </tr></thead>
                <tbody className="divide-y divide-border">
                  {runs.slice(-15).reverse().map((r) => (
                    <tr key={r.run_id} className="hover:bg-accent/30">
                      <td className="px-4 py-2 font-mono text-xs">{r.run_id}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{r.run_started_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-2"><Badge variant={r.status === "failed" ? "destructive" : "secondary"} className="text-[10px] uppercase">{r.status}</Badge></td>
                      <td className="px-4 py-2 font-mono text-xs text-right">{r.row_count?.toLocaleString()}</td>
                      <td className="px-4 py-2 font-mono text-xs text-right">{r.runtime_seconds}s</td>
                      <td className="px-4 py-2 font-mono text-xs text-right">{r.freshness_lag_minutes}m</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="quality" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border p-4 rounded-sm" data-testid="null-rate-chart">
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Null Rate</h3>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={recentNullRates}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "2px", fontSize: "11px", fontFamily: "JetBrains Mono" }} />
                    <Line type="monotone" dataKey="value" stroke="#D97706" strokeWidth={1.5} dot={{ r: 1.5 }} />
                    <Line type="monotone" dataKey="threshold" stroke="#DC2626" strokeWidth={1} strokeDasharray="4 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-card border border-border p-4 rounded-sm" data-testid="dup-rate-chart">
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Duplicate Rate</h3>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={recentDupRates}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "2px", fontSize: "11px", fontFamily: "JetBrains Mono" }} />
                    <Line type="monotone" dataKey="value" stroke="#2563EB" strokeWidth={1.5} dot={{ r: 1.5 }} />
                    <Line type="monotone" dataKey="threshold" stroke="#DC2626" strokeWidth={1} strokeDasharray="4 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="schema" className="mt-4">
          <div className="bg-card border border-border rounded-sm" data-testid="schema-history">
            <div className="px-4 py-3 border-b border-border"><h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Schema History</h3></div>
            <div className="divide-y divide-border">
              {schemas.map((s) => {
                let cols = [];
                try { cols = JSON.parse(s.schema_json); } catch (err) { console.error(err); }
                return (
                  <div key={s.snapshot_id} className="px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-[10px]">{s.schema_version}</Badge>
                        <span className="text-xs text-muted-foreground font-mono">{s.captured_at?.slice(0, 10)}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{cols.length} columns</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cols.map((c) => (
                        <code key={c.name} className="text-[10px] bg-muted px-1.5 py-0.5 rounded-sm font-mono">{c.name} <span className="text-muted-foreground">{c.type}</span></code>
                      ))}
                    </div>
                  </div>
                );
              })}
              {schemas.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground font-mono">[ NO SCHEMA SNAPSHOTS ]</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="incidents" className="mt-4">
          <div className="bg-card border border-border rounded-sm" data-testid="pipeline-incidents">
            <div className="divide-y divide-border">
              {incidents.map((inc) => (
                <Link key={inc.incident_id} to={`/incidents?pipeline_id=${data.pipeline_id}`} className="block px-4 py-3 hover:bg-accent/30 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`status-dot ${inc.severity}`} />
                      <span className="font-medium text-sm">{inc.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={inc.status === "resolved" ? "secondary" : "outline"} className="text-[10px] uppercase">{inc.status}</Badge>
                      <Badge variant={inc.severity === "critical" ? "destructive" : "outline"} className="text-[10px] uppercase">{inc.severity}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{inc.summary}</p>
                </Link>
              ))}
              {incidents.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground font-mono">[ NO INCIDENTS ]</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="lineage" className="mt-4">
          <div className="bg-card border border-border p-4 rounded-sm" data-testid="downstream-assets">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Downstream Impacted Assets</h3>
            {impacted.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {impacted.map((a) => (
                  <div key={a} className="flex items-center gap-1.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 px-3 py-1.5 rounded-sm">
                    <GitBranch className="w-3 h-3 text-red-500" />
                    <span className="text-sm font-mono">{a}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground font-mono">[ NO DOWNSTREAM DEPENDENCIES FOUND ]</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
